import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { User } from "@supabase/supabase-js";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  isAnonymous: boolean;
}

function mapUser(u: User | null): AuthUser | null {
  if (!u) return null;
  const meta = u.user_metadata ?? {};
  return {
    id: u.id,
    name: meta.username ?? meta.full_name ?? meta.name ?? "",
    email: u.email ?? "",
    avatarUrl: meta.avatar_url ?? null,
    isAnonymous: (u as User & { is_anonymous?: boolean }).is_anonymous ?? false,
  };
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;

        if (data.session?.user) {
          setUser(mapUser(data.session.user));
        } else {
          // No session — sign in anonymously, with up to 3 retries on failure
          let signedIn = false;
          for (let attempt = 0; attempt < 3; attempt++) {
            if (cancelled) return;
            if (attempt > 0) {
              await new Promise<void>((r) => setTimeout(r, 1000 * attempt));
            }
            const { data: anonData, error } = await supabase.auth.signInAnonymously();
            if (!error && anonData.user) {
              setUser(mapUser(anonData.user));
              signedIn = true;
              break;
            }
          }
          if (!signedIn && !cancelled) {
            setUser(null);
          }
        }
      } catch {
        // swallow — loading will still be cleared below
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(mapUser(session?.user ?? null));
    });

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    // Re-sign in anonymously so app stays functional
    await supabase.auth.signInAnonymously();
  };

  return { user, logout, loading };
}
